import React from 'react';
import { LuHeart, LuShoppingCart, LuTrash2, LuEye } from 'react-icons/lu';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useWishlist } from '../context/WishlistContext';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';

const Wishlist = () => {
  const { user } = useAuth();
  const { addToCart } = useCart();
  const { wishlistItems, removeFromWishlist, clearWishlist } = useWishlist();

  const addToCartFromWishlist = (item) => {
    addToCart(item);
    toast.success('Added to cart');
  };

  if (!user) {
    return (
      <div className="sc-container pb-16 pt-8 md:pt-10">
        <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500">
          <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
            Home
          </Link>
          <span>/</span>
          <span className="font-semibold text-slate-700">Wishlist</span>
        </nav>
        <div className="sc-soft-card p-10 text-center sm:p-12">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-[#DB4444]/10">
            <LuHeart className="h-12 w-12 text-[#DB4444]" />
          </div>
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2">Please sign in</h2>
          <p className="mb-6 text-slate-600">You need to be logged in to view your wishlist.</p>
          <Link to="/login" className="sc-btn-primary sc-hover-smooth inline-block">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="sc-container pb-16 pt-8 md:pt-10">
      <nav className="mb-7 flex items-center gap-2 text-sm text-slate-500">
        <Link to="/" className="sc-hover-smooth hover:text-[#DB4444]">
          Home
        </Link>
        <span>/</span>
        <span className="font-semibold text-slate-700">Wishlist</span>
      </nav>

      <section className="sc-soft-card relative mb-6 overflow-hidden p-6 sm:p-7">
        <div className="absolute -right-20 -top-20 h-52 w-52 rounded-full bg-[#DB4444]/10 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#DB4444]/10">
              <LuHeart className="h-6 w-6 text-[#DB4444]" />
            </div>
            <div>
              <p className="mb-1 text-xs font-bold uppercase tracking-[0.24em] text-[#DB4444]">Saved Picks</p>
              <h1 className="text-3xl font-black text-[#1A1A1A]">My Wishlist</h1>
              <p className="text-sm text-slate-600">
                {wishlistItems.length} {wishlistItems.length === 1 ? 'item' : 'items'} saved
              </p>
            </div>
          </div>
          {wishlistItems.length > 0 && (
            <button onClick={clearWishlist} className="sc-btn-ghost sc-hover-smooth inline-flex items-center gap-2">
              <LuTrash2 className="h-4 w-4" />
              Clear All
            </button>
          )}
        </div>
      </section>

      {wishlistItems.length === 0 ? (
        <div className="sc-soft-card p-10 text-center sm:p-12">
          <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-slate-100">
            <LuHeart className="h-12 w-12 text-slate-400" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-[#1A1A1A]">Your wishlist is empty</h2>
          <p className="mb-6 text-slate-600">
            Save items you like to your wishlist. Review them anytime and move them to your cart.
          </p>
          <Link to="/products" className="sc-btn-primary sc-hover-smooth inline-block">
            Start Shopping
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {wishlistItems.map((item) => (
            <article key={item.id} className="sc-soft-card overflow-hidden">
              <div className="relative group border-b border-slate-200 bg-white">
                <img
                  src={item.image || item.imageUrl || '/placeholder-product.jpg'}
                  alt={item.name}
                  className="h-48 w-full object-contain"
                />

                <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/40 opacity-0 transition-opacity group-hover:opacity-100">
                  <Link
                    to={`/product/${item.id}`}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white text-slate-800 sc-hover-smooth"
                    title="View Details"
                  >
                    <LuEye className="h-4 w-4" />
                  </Link>
                  <button
                    onClick={() => addToCartFromWishlist(item)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#DB4444] text-white sc-hover-smooth"
                    title="Add to Cart"
                  >
                    <LuShoppingCart className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => removeFromWishlist(item.id)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-slate-900 text-white sc-hover-smooth"
                    title="Remove from Wishlist"
                  >
                    <LuTrash2 className="h-4 w-4" />
                  </button>
                </div>

                <button
                  onClick={() => removeFromWishlist(item.id)}
                  className="absolute right-2 top-2 rounded-full bg-white p-1.5 text-[#DB4444] shadow-md transition-colors hover:bg-red-50 sm:hidden"
                >
                  <LuTrash2 className="h-4 w-4" />
                </button>
              </div>

              <div className="p-4">
                <Link to={`/product/${item.id}`}>
                  <h3 className="mb-2 line-clamp-2 font-semibold text-[#1A1A1A] sc-hover-smooth hover:text-[#DB4444]">
                    {item.name}
                  </h3>
                </Link>

                <div className="mb-3 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg font-bold text-[#1A1A1A]">৳{item.price}</span>
                    {item.originalPrice && item.originalPrice > item.price && (
                      <span className="text-sm text-slate-500 line-through">৳{item.originalPrice}</span>
                    )}
                  </div>
                  {item.originalPrice && item.originalPrice > item.price && (
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                      {Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}% OFF
                    </span>
                  )}
                </div>

                <div className="mb-3">
                  {item.stock > 0 ? (
                    <span className="rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700">
                      In Stock ({item.stock} available)
                    </span>
                  ) : (
                    <span className="rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700">Out of Stock</span>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => addToCartFromWishlist(item)}
                    disabled={item.stock === 0}
                    className="sc-btn-primary sc-hover-smooth flex flex-1 items-center justify-center gap-2 !rounded-xl !px-3 !py-2 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <LuShoppingCart className="h-4 w-4" />
                    Add to Cart
                  </button>
                  <button
                    onClick={() => removeFromWishlist(item.id)}
                    className="hidden rounded-xl border border-slate-200 bg-slate-100 px-3 py-2 text-sm text-slate-700 sc-hover-smooth hover:border-[#DB4444] hover:text-[#DB4444] sm:block"
                  >
                    <LuTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      {wishlistItems.length > 0 && (
        <section className="sc-soft-card mt-8 p-6">
          <h3 className="mb-2 text-lg font-semibold text-[#1A1A1A]">Wishlist Tips</h3>
          <ul className="space-y-1 text-sm text-slate-600">
            <li>Items in your wishlist are saved for 90 days</li>
            <li>Share your wishlist with friends and family</li>
            <li>Get notified when prices drop on wishlist items</li>
            <li>Easily move items to your cart when you're ready to buy</li>
          </ul>
        </section>
      )}
    </div>
  );
};

export default Wishlist;
